import {
  InteractiveElementType,
  Slide,
  SlideTemplateType,
  TextAlignment,
} from '@/client/courses';
import { ContentEmailComponent } from '@/components/courses/elements/ContentEmailComponent';
import { ContentSMSComponent } from '@/components/courses/elements/ContentSMSComponent';
import { SlideImageComponent } from '@/components/slides/elements/SlideImageComponent';
import { useAccountVariables } from '@/hooks/query/account-variables.hooks';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { RichTextRenderer } from '@/ui/rich-text-renderer';
import { FlexContainer } from '@/ui/styled-ui';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

const ContentContainer = styled.div<{ isChapter?: boolean }>`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  padding: 0;
  ${({ isChapter }) =>
    isChapter &&
    `
    display: flex;
    align-items: center;
    min-height: 420px;
    height: 100%;
    flex: 1;
  `}
`;

const SlideContentContainer = styled(FlexContainer)<{
  textAlignment: TextAlignment;
}>`
  min-height: 0;
  align-items: flex-start;
  overflow: visible;

  ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER &&
    `
    text-align: center;
  `}
  ${({ textAlignment }) =>
    textAlignment === TextAlignment.RIGHT &&
    `
    text-align: right;
  `}
`;

const ImageGridContainer = styled.div<{
  textAlignment: TextAlignment;
  isGallery?: boolean;
  isChapter?: boolean;
}>`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: auto;
  grid-auto-columns: ${({ isGallery }) =>
    isGallery ? 'minmax(0, 1fr)' : '1fr'};
  gap: var(--small-padding);
  width: 100%;
  max-width: ${({ isGallery }) => (isGallery ? '100%' : '375px')};
  min-width: 0;
  box-sizing: border-box;
  align-items: start;
  justify-items: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'end'
      : 'start'};
  justify-content: ${({ textAlignment }) =>
    textAlignment === TextAlignment.CENTER
      ? 'center'
      : textAlignment === TextAlignment.RIGHT
      ? 'flex-end'
      : 'flex-start'};
  align-self: ${({ isChapter }) => (isChapter ? 'center' : 'flex-start')};
  margin: 0;
  padding: 0;
  overflow: visible;

  > * {
    height: auto !important;
    min-height: 0;
    overflow: visible !important;
    max-height: none !important;
  }
`;

const Footnote = styled.p`
  font-size: var(--xsmall-font-size);
  margin-top: var(--medium-padding);
  width: 100%;
  white-space: pre-wrap;
`;

type PopupSlideTemplateContentProps = {
  slide: Slide;
  rtl?: boolean;
};

export const PopupSlideTemplateContent: React.FC<
  PopupSlideTemplateContentProps
> = ({ slide, rtl }) => {
  const { t } = useTranslation();
  const currentAccount = useSelector(selectCurrentAccount);
  const { canUseFeature } = useFeatureFlag();
  const variablesEnabled = canUseFeature(
    PostHogFeatureFlags.TRAINING_VARIABLES,
  );
  const { variables: accountVariables } = useAccountVariables(
    currentAccount?.id,
    { enabled: variablesEnabled },
  );
  const isChapter = slide.templateType === SlideTemplateType.CHAPTER;
  const isSummary = slide.templateType === SlideTemplateType.SUMMARY;
  const rawTextAlignment = slide.textAlignment;
  const effectiveTextAlignment = rtl
    ? rawTextAlignment === TextAlignment.CENTER
      ? TextAlignment.CENTER
      : TextAlignment.RIGHT
    : rawTextAlignment ?? TextAlignment.LEFT;

  return (
    <>
      <ContentContainer isChapter={isChapter}>
        <SlideContentContainer
          gap={22}
          justify="space-between"
          align={isChapter ? 'center' : 'flex-start'}
          height={isChapter ? '100%' : 'auto'}
          flex={isChapter ? '1' : '0'}
          textAlignment={effectiveTextAlignment}
        >
          <FlexContainer
            direction="column"
            height={isChapter ? '100%' : 'auto'}
            justify={isChapter ? 'center' : 'flex-start'}
            align={
              effectiveTextAlignment === TextAlignment.CENTER
                ? 'center'
                : effectiveTextAlignment === TextAlignment.RIGHT
                ? 'flex-end'
                : 'flex-start'
            }
          >
            <RichTextRenderer
              isTitle
              content={slide.headline || t('content.slide.title')}
              textAlignment={effectiveTextAlignment}
              rtl={rtl}
              accountVariables={accountVariables}
            />

            <RichTextRenderer
              content={slide.html || ''}
              className={isSummary ? 'rich-text-summary' : ''}
              textAlignment={effectiveTextAlignment}
              rtl={rtl}
              accountVariables={accountVariables}
            />
          </FlexContainer>

          {slide.interactiveElement ? (
            slide.interactiveElement.type === InteractiveElementType.EMAIL ? (
              <ContentEmailComponent
                key={slide.id}
                id={slide.id}
                interactiveElement={slide.interactiveElement}
                readOnly={true}
              />
            ) : slide.interactiveElement.type === InteractiveElementType.SMS ? (
              <ContentSMSComponent
                key={slide.id}
                id={slide.id}
                interactiveElement={slide.interactiveElement}
                readOnly={true}
                rtl={rtl}
              />
            ) : null
          ) : (
            slide.images &&
            slide.images.length > 0 &&
            [
              SlideTemplateType.TEXT_IMAGE,
              SlideTemplateType.CHAPTER,
              SlideTemplateType.SUMMARY,
            ].includes(slide.templateType) && (
              <ImageGridContainer
                isChapter={isChapter}
                textAlignment={effectiveTextAlignment}
              >
                {slide.images.map((image, index) => (
                  <SlideImageComponent
                    key={index}
                    image={image}
                    currentEditingSlide={slide}
                    textAlignment={effectiveTextAlignment}
                  />
                ))}
              </ImageGridContainer>
            )
          )}
        </SlideContentContainer>
      </ContentContainer>

      {slide.images &&
        slide.images.length > 0 &&
        slide.templateType === SlideTemplateType.GALLERY && (
          <ContentContainer>
            <ImageGridContainer
              textAlignment={effectiveTextAlignment}
              isGallery={true}
              style={{ marginTop: '16px' }}
            >
              {slide.images.map((image, index) => (
                <SlideImageComponent
                  key={index}
                  image={image}
                  currentEditingSlide={slide}
                  textAlignment={effectiveTextAlignment}
                  isGallery={true}
                />
              ))}
            </ImageGridContainer>
          </ContentContainer>
        )}

      {slide.footnote &&
        [SlideTemplateType.TEXT_IMAGE, SlideTemplateType.SUMMARY].includes(
          slide.templateType,
        ) && (
          <ContentContainer>
            <Footnote>{slide.footnote}</Footnote>
          </ContentContainer>
        )}
    </>
  );
};
